/**
 * Low-level OPFS directory-tree operations shared by the stores in this folder
 * (见 18): the delivered-bundle cache and the linked-module area.
 *
 * Only mechanics live here — creating parents on the way down, the exclusive
 * writable lock, and OPFS's non-idempotent delete. **Policy stays with each
 * store**: content addressing and marker validity belong to
 * `bundle-store-opfs.ts`, and neither of them belongs to the linked area.
 *
 * As with `module-fs-opfs.ts`, the handle type is narrowed to what is actually
 * used rather than taking the DOM type, which is what lets the tests drive all
 * of this with a small in-memory fake.
 */
import { parseJsonText } from '@hibro/shared';

import type { FileHandleLike } from './module-fs-opfs';

/** The slice of `FileSystemDirectoryHandle` these stores use. Self-recursive. */
export interface OpfsDirectoryHandle {
  getDirectoryHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<OpfsDirectoryHandle>;
  getFileHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<FileHandleLike>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  values(): AsyncIterable<{ kind: string; name: string }>;
}

/** Whether a rejection is OPFS's "no such file or directory". */
export function isNotFound(err: unknown): boolean {
  return err instanceof Error && err.name === 'NotFoundError';
}

/** The real origin-private root, typed as the narrowed handle. */
export function opfsRoot(): Promise<OpfsDirectoryHandle> {
  return navigator.storage.getDirectory();
}

/**
 * A name nothing ever creates, so a lookup of it can only ever be answered with
 * "not found". The leading `.` is the same guard the bundle cache's staging
 * prefix relies on: a module id cannot start with one, so this can never collide
 * with a real entry.
 */
const PROBE_DIRNAME = '.hibro-probe';

/**
 * Why the origin-private filesystem is unusable, or `undefined` if it works
 * (见 15 / 18). Never rejects — the answer *is* the return value.
 *
 * Worth its own probe because `navigator.storage.estimate()` cannot answer this:
 * that reads the quota layer and never touches File System Access, so it reports
 * a perfectly healthy budget while every OPFS call rejects. Measured in the
 * field: `AbortError: The user aborted a request.`, which is Chromium's default
 * message for "the operation failed for some unspecified reason" — nobody
 * aborted anything, and the name sends every reader down the wrong path.
 *
 * The test is a **read** of {@link PROBE_DIRNAME}, and `NotFoundError` is the
 * pass: reaching a truthful "no such directory" means the whole path works.
 * Deliberately `getDirectoryHandle` rather than `values()` — that is the call
 * real failures land on, and probing a different one would prove the wrong
 * thing. Nothing is created, so nothing is left to clean up.
 */
export async function opfsUnusableReason(
  getRoot: () => Promise<OpfsDirectoryHandle> = opfsRoot,
): Promise<string | undefined> {
  try {
    const root = await getRoot();
    await root.getDirectoryHandle(PROBE_DIRNAME, { create: false });
  } catch (err) {
    if (isNotFound(err)) {
      return undefined;
    }
    return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  }
  return `a ${PROBE_DIRNAME} directory exists in the origin-private root`;
}

/** Walk down `segments`, optionally creating each level. */
export async function descend(
  dir: OpfsDirectoryHandle,
  segments: readonly string[],
  create: boolean,
): Promise<OpfsDirectoryHandle> {
  let handle = dir;
  for (const segment of segments) {
    handle = await handle.getDirectoryHandle(segment, { create });
  }
  return handle;
}

/** Names of the immediate subdirectories of `dir`. */
export async function listDirectoryNames(
  dir: OpfsDirectoryHandle,
): Promise<string[]> {
  const names: string[] = [];
  for await (const child of dir.values()) {
    if (child.kind === 'directory') {
      names.push(child.name);
    }
  }
  return names;
}

/** Write one file, creating parent directories as needed. */
export async function writeFileAt(
  dir: OpfsDirectoryHandle,
  segments: readonly string[],
  data: Uint8Array,
): Promise<void> {
  const parent = await descend(dir, segments.slice(0, -1), true);
  const handle = await parent.getFileHandle(segments[segments.length - 1], {
    create: true,
  });
  const writable = await handle.createWritable();
  try {
    await writable.write(data as Uint8Array<ArrayBuffer>);
    await writable.close();
  } catch (err) {
    // A writable holds an **exclusive lock** until closed or aborted. Leaking it
    // turns a recoverable failure (quota, most likely) into a file that can never
    // be written again — every later attempt failing on the stale lock.
    await writable.abort?.(err).catch(() => undefined);
    throw err;
  }
}

/**
 * A file being written incrementally. Wraps the exclusive-lock discipline
 * {@link writeFileAt} applies in one shot, for the callers that cannot hold the
 * whole file in memory — a streamed bundle unpack (见 12) writes each entry as
 * its bytes arrive off the network.
 */
export interface FileWriter {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  /** Release the lock without committing. Idempotent. */
  abort(reason?: unknown): Promise<void>;
}

/**
 * Open one file for streaming writes, creating parent directories as needed.
 *
 * The caller **must** reach `close()` or `abort()`: a writable holds an
 * exclusive lock on the file until then, and leaking it makes that path
 * unwritable for the rest of the session (see {@link writeFileAt}).
 */
export async function openFileWriter(
  dir: OpfsDirectoryHandle,
  segments: readonly string[],
): Promise<FileWriter> {
  const parent = await descend(dir, segments.slice(0, -1), true);
  const handle = await parent.getFileHandle(segments[segments.length - 1], {
    create: true,
  });
  const writable = await handle.createWritable();
  let settled = false;
  return {
    write: (chunk: Uint8Array) =>
      writable.write(chunk as Uint8Array<ArrayBuffer>),
    close: async () => {
      if (!settled) {
        settled = true;
        await writable.close();
      }
    },
    abort: async (reason?: unknown) => {
      if (!settled) {
        settled = true;
        await writable.abort?.(reason).catch(() => undefined);
      }
    },
  };
}

/** Paths (as segment arrays) of every file under `dir`, depth first. */
export async function listFilesRecursively(
  dir: OpfsDirectoryHandle,
  base: readonly string[] = [],
): Promise<string[][]> {
  const found: string[][] = [];
  for await (const child of dir.values()) {
    const segments = [...base, child.name];
    if (child.kind === 'directory') {
      found.push(
        ...(await listFilesRecursively(
          await dir.getDirectoryHandle(child.name),
          segments,
        )),
      );
    } else {
      found.push(segments);
    }
  }
  return found;
}

/** A file handle Chromium can relink into another directory without copying. */
interface MovableFileHandle extends FileHandleLike {
  move(parent: OpfsDirectoryHandle, name: string): Promise<void>;
}

function canMove(handle: FileHandleLike): handle is MovableFileHandle {
  return typeof (handle as Partial<MovableFileHandle>).move === 'function';
}

/**
 * Move one file from `fromDir` to the same relative path under `toDir`.
 *
 * `FileSystemFileHandle.move` is metadata-only, which is what makes staging an
 * unpack and then placing it affordable. It is not universally implemented
 * (and directory moves are not implemented anywhere dependable, which is why
 * this is per file), so the fallback is a streamed copy — bounded memory, just
 * twice the write I/O.
 */
export async function moveFileInto(
  fromDir: OpfsDirectoryHandle,
  toDir: OpfsDirectoryHandle,
  segments: readonly string[],
): Promise<void> {
  const name = segments[segments.length - 1];
  const source = await (
    await descend(fromDir, segments.slice(0, -1), false)
  ).getFileHandle(name);
  const parent = await descend(toDir, segments.slice(0, -1), true);
  if (canMove(source)) {
    await source.move(parent, name);
    return;
  }
  const reader = (await source.getFile()).stream().getReader();
  const writer = await openFileWriter(toDir, segments);
  try {
    for (;;) {
      const next = await reader.read();
      if (next.done) {
        break;
      }
      await writer.write(next.value);
    }
    await writer.close();
  } catch (err) {
    await writer.abort(err);
    throw err;
  } finally {
    reader.releaseLock();
  }
  await removeIfPresent(
    await descend(fromDir, segments.slice(0, -1), false),
    name,
  );
}

/**
 * Copy one file from `fromDir` to the same relative path under `toDir`.
 *
 * Unlike {@link moveFileInto}, the source stays. Pending promotion needs that:
 * a failed placement must leave the pending tree intact so the next open can
 * try again. Memory stays bounded — the bytes stream, they are not buffered.
 */
export async function copyFileInto(
  fromDir: OpfsDirectoryHandle,
  toDir: OpfsDirectoryHandle,
  segments: readonly string[],
): Promise<void> {
  const name = segments[segments.length - 1];
  const source = await (
    await descend(fromDir, segments.slice(0, -1), false)
  ).getFileHandle(name);
  const reader = (await source.getFile()).stream().getReader();
  const writer = await openFileWriter(toDir, segments);
  try {
    for (;;) {
      const next = await reader.read();
      if (next.done) {
        break;
      }
      await writer.write(next.value);
    }
    await writer.close();
  } catch (err) {
    await writer.abort(err);
    throw err;
  } finally {
    reader.releaseLock();
  }
}

/** Read one file, or `undefined` if it (or any parent) is absent. */
export async function readFileAt(
  dir: OpfsDirectoryHandle,
  segments: readonly string[],
): Promise<Uint8Array | undefined> {
  if (segments.length === 0) {
    return undefined;
  }
  try {
    const parent = await descend(dir, segments.slice(0, -1), false);
    const handle = await parent.getFileHandle(segments[segments.length - 1]);
    return new Uint8Array(await (await handle.getFile()).arrayBuffer());
  } catch (err) {
    if (isNotFound(err)) {
      return undefined;
    }
    throw err;
  }
}

/**
 * Read and parse one JSON file, or `undefined` if absent or malformed. A read
 * that *fails* rejects: {@link readFileAt} already reports an absent file as
 * `undefined`, so swallowing here could only ever hide a real I/O failure —
 * which is exactly the failure that must not come back as "this bundle has no
 * marker" or "this linked module has no manifest", i.e. as a module that was
 * never installed.
 *
 * Malformed content is different and stays `undefined`: unparseable JSON is not
 * a valid marker/manifest, and refusing it is the fail-closed rule the stores
 * are built on.
 */
export async function readJsonAt(
  dir: OpfsDirectoryHandle,
  segments: readonly string[],
): Promise<unknown> {
  const bytes = await readFileAt(dir, segments);
  if (!bytes) {
    return undefined;
  }
  try {
    return parseJsonText(new TextDecoder().decode(bytes));
  } catch {
    return undefined;
  }
}

/**
 * Remove an entry, treating "already gone" as success. OPFS itself is not
 * idempotent here — a second `removeEntry` rejects with `NotFoundError`.
 */
export async function removeIfPresent(
  dir: OpfsDirectoryHandle,
  name: string,
): Promise<void> {
  try {
    await dir.removeEntry(name, { recursive: true });
  } catch (err) {
    if (!isNotFound(err)) {
      throw err;
    }
  }
}
